import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { studentApi } from '@/api';
import { Badge, Button, EmptyState, Icon, LinkButton, useToast } from '@/components/ui';
import { demandLabel, verdictOf } from '@/features/career/cvCheck';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';

import {
  CV_CHECK_LINK,
  formatGap,
  gapContext,
  groupByKind,
  severityOf,
  sortBySeverity,
} from './gaps';

/**
 * The four parts of the resume gap analysis, as shown in the "Resume gap
 * analysis" section of the CV page.
 *
 * Everything shown is counted by rules on the server; these only lay it out.
 */

/** "4 to review" in the severity's tone, or "All clear". */
export function CountBadge({ counts }) {
  const total = (counts?.important ?? 0) + (counts?.suggested ?? 0);
  if (!total) {
    return (
      <Badge tone="success">
        <Icon name="check" size={11} />
        All clear
      </Badge>
    );
  }
  return <Badge tone={counts.important ? 'warning' : 'info'}>{total} to review</Badge>;
}

export function SeverityChip({ severity }) {
  const meta = severityOf(severity);
  return (
    <Badge tone={meta.tone} dot>
      {meta.label}
    </Badge>
  );
}

function AllClear({ title, description }) {
  return (
    <div className="bg-success-50/60 ring-success-100 rounded-input flex items-start gap-3 px-3.5 py-3 ring-1 ring-inset">
      <span className="bg-success-100 text-success-700 grid size-6 shrink-0 place-items-center rounded-full">
        <Icon name="check" size={13} />
      </span>
      <div className="min-w-0">
        <p className="text-success-800 text-sm font-medium">{title}</p>
        {description && <p className="text-ink-muted mt-0.5 text-xs">{description}</p>}
      </div>
    </div>
  );
}

function PartFooter({ children }) {
  return (
    <div className="border-line mt-3 flex flex-wrap items-center justify-between gap-2 border-t pt-3">
      {children}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Summary
// --------------------------------------------------------------------------- //
/** Gap counts by severity and the skill fit, as three small tiles. */
export function GapCounts({ summary, skills }) {
  const verdict = summary.skill_score !== null && skills.fit ? verdictOf(skills.fit) : null;
  const tiles = [
    {
      label: 'Important',
      value: summary.important,
      tone: summary.important ? 'text-warning-700' : 'text-success-700',
      icon: 'alert',
      iconTone: 'bg-warning-50 text-warning-700',
    },
    {
      label: 'Suggested',
      value: summary.suggested,
      tone: 'text-ink',
      icon: 'info',
      iconTone: 'bg-info-50 text-info-700',
    },
    {
      label: 'Skill fit',
      value: summary.skill_score !== null ? `${summary.skill_score}%` : '—',
      tone: 'text-ink',
      icon: 'target',
      iconTone: 'bg-brand-50 text-brand-700',
      hint: verdict ? verdict.label : 'No goal set',
    },
  ];
  return (
    <dl className="grid w-full grid-cols-3 gap-2 sm:w-auto">
      {tiles.map((tile) => (
        <div
          key={tile.label}
          className="border-line bg-surface rounded-input min-w-0 border px-3 py-2 sm:min-w-28"
        >
          <dt className="text-ink-muted flex items-center gap-1.5 text-[11px] font-medium tracking-wide uppercase">
            <span
              aria-hidden="true"
              className={cn(
                'hidden size-5 shrink-0 place-items-center rounded-md sm:grid',
                tile.iconTone,
              )}
            >
              <Icon name={tile.icon} size={11} />
            </span>
            <span className="truncate">{tile.label}</span>
          </dt>
          <dd className={cn('mt-1 text-xl font-semibold tabular-nums', tile.tone)}>{tile.value}</dd>
          {tile.hint && <dd className="text-ink-muted truncate text-[11px]">{tile.hint}</dd>}
        </div>
      ))}
    </dl>
  );
}

// --------------------------------------------------------------------------- //
// 1. Skill gaps
// --------------------------------------------------------------------------- //
export function SkillGapsBody({ part }) {
  const fit = part.fit;
  const verdict = fit ? verdictOf(fit) : null;
  const missing = sortBySeverity(part.missing);

  if (part.status === 'no_goal') {
    return (
      <EmptyState
        compact
        icon={<Icon name="route" size={16} />}
        title="Set your career goal"
        description={part.message}
        action={
          <LinkButton to={paths.student.career} size="sm" variant="tintBrand">
            <Icon name="route" size={14} />
            Go to Career guidance
          </LinkButton>
        }
      />
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {part.category && <Badge tone="brand">{part.category.name}</Badge>}
        {part.basis_label && <Badge tone="neutral">{part.basis_label}</Badge>}
        {verdict && fit.score !== null && (
          <Badge tone={verdict.tone} dot>
            {verdict.label} · {fit.score}%
          </Badge>
        )}
      </div>
      <p className="text-ink mt-2 text-sm">{part.message}</p>

      {fit && fit.score !== null && (
        <>
          {missing.length === 0 ? (
            <div className="mt-3">
              <AllClear
                title="No skill gaps found"
                description="Your CV names every skill these listings ask for."
              />
            </div>
          ) : (
            <ul className="divide-line mt-3 divide-y">
              {missing.map((item) => (
                <li
                  key={item.skill.id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 first:pt-0 last:pb-0"
                >
                  <span className="text-ink min-w-0 flex-1 text-sm font-medium break-words">
                    {item.skill.name}
                  </span>
                  <SeverityChip severity={item.severity} />
                  <span className="text-ink-muted w-full text-xs">
                    {demandLabel(item, fit.open_listings)}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {fit.found.length > 0 && (
            <div className="mt-3">
              <p className="text-ink-muted text-[11px] font-semibold tracking-wide uppercase">
                Already on your CV
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {fit.found.map((item) => (
                  <Badge key={item.skill.id} tone="success">
                    <Icon name="check" size={11} />
                    {item.skill.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <PartFooter>
        <p className="text-ink-muted min-w-0 flex-[1_1_12rem] text-xs">
          Want written suggestions for this goal? Career guidance can check this CV with AI.
        </p>
        <LinkButton to={CV_CHECK_LINK} size="sm" variant="tintBrand">
          <Icon name="sparkles" size={14} />
          Check my CV against this goal
        </LinkButton>
      </PartFooter>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// 2. Timeline gaps
// --------------------------------------------------------------------------- //
export function TimelineBody({ part }) {
  return (
    <div>
      {part.status === 'no_dates' ? (
        <EmptyState
          compact
          icon={<Icon name="calendar" size={16} />}
          title="No dates to check"
          description={`${part.message} Add a start and end month to each entry so recruiters can follow your path.`}
        />
      ) : part.gaps.length === 0 ? (
        <AllClear title="No timeline gaps found" description={part.message} />
      ) : (
        <ol className="space-y-2.5">
          {part.gaps.map((gap) => (
            <li
              key={`${gap.start}-${gap.end}`}
              className="border-line rounded-input border-l-info-500 border border-l-[3px] px-3.5 py-2.5"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-ink text-sm font-semibold tabular-nums">{formatGap(gap)}</p>
                <SeverityChip severity={gap.severity} />
              </div>
              {gapContext(gap) && (
                <p className="text-ink-muted mt-0.5 text-xs break-words">{gapContext(gap)}</p>
              )}
              <p className="text-ink mt-1.5 text-[13px]">{gap.advice}</p>
            </li>
          ))}
        </ol>
      )}
      {part.status !== 'no_dates' && part.undated_entries > 0 && (
        <p className="text-ink-muted mt-3 text-xs">
          {part.undated_entries} {part.undated_entries === 1 ? 'entry has' : 'entries have'} no
          dates we could read, so {part.undated_entries === 1 ? 'it is' : 'they are'} not on the
          timeline.
        </p>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// 3. Missing or weak sections
// --------------------------------------------------------------------------- //
export function SectionsBody({ part }) {
  const items = sortBySeverity(part.items);
  return (
    <div>
      {items.length === 0 ? (
        <AllClear
          title="No missing or weak sections"
          description="This CV has every section these checks look for."
        />
      ) : (
        <ul className="divide-line divide-y">
          {items.map((item) => (
            <li key={item.key} className="py-3 first:pt-0 last:pb-0">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="text-ink min-w-0 flex-1 text-sm font-semibold">{item.title}</p>
                <SeverityChip severity={item.severity} />
              </div>
              {item.detail && <p className="text-ink-muted mt-0.5 text-xs">{item.detail}</p>}
              <p className="text-ink-muted mt-1 text-[13px]">{item.why}</p>
              <p className="text-ink mt-1 text-[13px]">
                <span className="text-brand-700 font-semibold">Fix: </span>
                {item.fix}
              </p>
            </li>
          ))}
        </ul>
      )}
      <p className="text-ink-muted mt-3 text-xs">
        {part.passed} of {part.checked} checks passed, counting the CV score's checklist and a few
        more rules.
      </p>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// 4. CV vs profile differences
// --------------------------------------------------------------------------- //
export function ProfileBody({ part, resumeId, parseStatus }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const cvOnly = groupByKind(part.cv_only);
  const profileOnly = groupByKind(part.profile_only);
  const hasReading = Boolean(part.parse_status ?? parseStatus);

  // A CV already read goes straight to its suggestions; one never read is
  // read first, exactly as the CV card's button does.
  const read = useMutation({
    mutationFn: () => studentApi.parseResume(resumeId),
    onSuccess: (parse) => {
      queryClient.setQueryData(['resume-parse', parse.resume_id], parse);
      void queryClient.invalidateQueries({ queryKey: ['student-resumes'] });
      navigate(paths.student.cvReview(parse.resume_id));
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not read the file')),
  });

  function fill() {
    if (hasReading) navigate(paths.student.cvReview(resumeId));
    else read.mutate();
  }

  if (cvOnly.length === 0 && profileOnly.length === 0) {
    return (
      <AllClear
        title="Your CV and profile match"
        description="Every skill, education and work entry on this CV is on your profile, and the other way round."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-ink text-sm font-semibold">On your CV, not on your profile</h3>
          {cvOnly.length > 0 && (
            <Button size="sm" variant="tintBrand" onClick={fill} isLoading={read.isPending}>
              <Icon name="userCheck" size={14} />
              Fill my profile from this CV
            </Button>
          )}
        </div>
        {cvOnly.length === 0 ? (
          <p className="text-ink-muted mt-1 text-xs">
            Nothing missing — your profile has everything this CV lists.
          </p>
        ) : (
          <MismatchGroups groups={cvOnly} />
        )}
      </div>

      <div className="border-line border-t pt-4">
        <h3 className="text-ink text-sm font-semibold">On your profile, not on this CV</h3>
        {profileOnly.length === 0 ? (
          <p className="text-ink-muted mt-1 text-xs">
            Nothing missing — this CV covers everything on your profile.
          </p>
        ) : (
          <>
            <p className="text-ink-muted mt-0.5 text-xs">
              Consider adding these to your CV where they are relevant to the roles you want.
            </p>
            <MismatchGroups groups={profileOnly} />
          </>
        )}
      </div>
    </div>
  );
}

function MismatchGroups({ groups }) {
  return (
    <div className="mt-2 space-y-2.5">
      {groups.map((group) => (
        <div key={group.kind}>
          <p className="text-ink-muted mb-1 flex items-center gap-1 text-[11px] font-semibold tracking-wide uppercase">
            <Icon name={group.icon} size={11} />
            {group.label}
          </p>
          {group.kind === 'skill' ? (
            <div className="flex flex-wrap gap-1.5">
              {group.items.map((item) => (
                <Badge key={item.label} tone="neutral">
                  {item.label}
                </Badge>
              ))}
            </div>
          ) : (
            <ul className="space-y-1">
              {group.items.map((item, index) => (
                <li
                  key={`${item.label}-${index}`}
                  className="bg-surface-muted/60 rounded-input px-3 py-1.5"
                >
                  <p className="text-ink text-[13px] font-medium break-words">{item.label}</p>
                  {item.detail && <p className="text-ink-muted text-xs">{item.detail}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

/** One part's body, by key. */
export function PartBody({ partKey, data, parseStatus }) {
  if (partKey === 'skills') return <SkillGapsBody part={data.skills} />;
  if (partKey === 'timeline') return <TimelineBody part={data.timeline} />;
  if (partKey === 'sections') return <SectionsBody part={data.sections} />;
  return <ProfileBody part={data.profile} resumeId={data.resume_id} parseStatus={parseStatus} />;
}
